/**
 * useInstallPrompt — Carnaf is a RETAINED app, so (unlike the visitor PWA,
 * which suppresses install) we actively but calmly invite it: home-screen
 * presence, offline document access, and — critically — push for renewal
 * reminders (which on iOS 16.4+ require the app be added to the Home Screen).
 *
 * Handles the two platforms:
 *  - Android/Chromium: capture `beforeinstallprompt`, defer it, expose prompt().
 *  - iOS Safari: no programmatic prompt; we detect it so the UI can show the
 *    Share → "Add to Home Screen" instruction.
 * Respects an already-installed (standalone) state and a dismissal cooldown.
 */
import { useCallback, useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'kol_install_dismissed_at';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari standalone flag
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const ios = /iphone|ipad|ipod/i.test(ua);
  // Exclude in-app webviews / other iOS browsers where A2HS differs.
  const webkit = /webkit/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return ios && webkit;
}

function recentlyDismissed(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    return at > 0 && Date.now() - at < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(recentlyDismissed());

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return false;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    if (choice.outcome === 'dismissed') dismiss();
    return choice.outcome === 'accepted';
  }, [deferred]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }, []);

  const ios = isIosSafari();
  // Eligible when not installed, not recently dismissed, and we either have a
  // captured Android prompt or are on iOS Safari (manual A2HS).
  const eligible = !installed && !dismissed && (Boolean(deferred) || ios);

  return { eligible, installed, dismissed, isIos: ios, canPrompt: Boolean(deferred), promptInstall, dismiss };
}
